import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { routes } from '@app/consts';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { BuySellPlanVerbose } from '@app/modules/time-off/models/buysell.model';
import { BuySellService } from '@app/modules/time-off/services/buy-sell.service';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { Culture } from '@app/shared/models/system-language/culture.model';
import { TranslateService } from '@ngx-translate/core';
import { BuySellPlanPublishHistoryDialogComponent } from '../buy-sell-plan-publish-history-dialog/buy-sell-plan-publish-history-dialog.component';

@Component({
  selector: 'app-buy-sell-plan-editor',
  templateUrl: './buy-sell-plan-editor.component.html',
  styleUrls: ['./buy-sell-plan-editor.component.scss']
})
export class BuySellPlanEditorComponent implements OnInit {
  buySellPlan: BuySellPlanVerbose;
  form: any;
  currentCulture: Culture;
  cultures: Culture[];
  formData: any;
  loadingCultures: boolean;
  fieldPermissions: any;
  formId: string = 'frm_1eWkHjkvz5sNXC';
  planFormData: any;
  planFormValid: any;
  planFormPristine: boolean = true;
  unsavedChanges: boolean = false;
  loading: boolean;

  constructor(
    private overlayService: OverlayService,
    public translate: TranslateService,
    private snackbarService: SnackbarService,
    private router: Router,
    private route: ActivatedRoute,
    private buySellService: BuySellService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    const id = this.getIdInURL();

    if (id) {
      this.getBuySellPlan(id);
    }
    else {
      this.buildForm();
    }
  }

  buildForm() {
    this.formData = {
      name: this.buySellPlan ? this.buySellPlan.name : null,
      description: this.buySellPlan ? this.buySellPlan.description : null,
      prorateOnFte: this.buySellPlan ? this.buySellPlan.prorateOnFte : false,
      permitSellingNegativeNumbers: this.buySellPlan ? this.buySellPlan.permitSellingNegativeNumbers : false,
      displayFutureAccrual: this.buySellPlan ? this.buySellPlan.displayFutureAccrual : false,
      maximumHoursEmployeeCanSell: this.buySellPlan ? this.buySellPlan.maximumHoursEmployeeCanSell : null,
      maximumHoursEmployeeCanBuy: this.buySellPlan ? this.buySellPlan.maximumHoursEmployeeCanBuy : null,
      timeOffType: this.buySellPlan ? this.buySellPlan.timeOffType?.id : null,
      hoursPerPeriodSalary: this.buySellPlan ? this.buySellPlan.hoursPerPeriodSalary : null,
      hoursPerPeriodMonthly: this.buySellPlan ? this.buySellPlan.hoursPerPeriodMonthly : null,
      hoursPerPeriodWeekly: this.buySellPlan ? this.buySellPlan.hoursPerPeriodWeekly : null,
      hoursPerPeriodDaily: this.buySellPlan ? this.buySellPlan.hoursPerPeriodDaily : null,
      allowBuy: this.buySellPlan ? this.buySellPlan.allowBuy : false,
      allowSell: this.buySellPlan ? this.buySellPlan.allowSell : false,
      publishSubject: this.buySellPlan ? this.buySellPlan.publishSubject : null,
      publishBody: this.buySellPlan ? this.buySellPlan.publishBody : null,
      approverSubject: this.buySellPlan ? this.buySellPlan.approverSubject : null,
      approverBody: this.buySellPlan ? this.buySellPlan.approverBody : null,
      approvedSubject: this.buySellPlan ? this.buySellPlan.approvedSubject : null,
      approvedBody: this.buySellPlan ? this.buySellPlan.approvedBody : null,
      deniedSubject: this.buySellPlan ? this.buySellPlan.deniedSubject : null,
      deniedBody: this.buySellPlan ? this.buySellPlan.deniedBody : null,
      finalApprovedBuySubject: this.buySellPlan ? this.buySellPlan.finalApprovedBuySubject : null,
      finalApprovedBuyBody: this.buySellPlan ? this.buySellPlan.finalApprovedBuyBody : null,
      finalApprovedSellSubject: this.buySellPlan ? this.buySellPlan.finalApprovedSellSubject : null,
      finalApprovedSellBody: this.buySellPlan ? this.buySellPlan.finalApprovedSellBody : null,
    };
  }

  getIdInURL(): string {
    let IdInURL: string;

    this.route.paramMap.subscribe(
      params => IdInURL = params.get('buySellPlanId')
    );

    return IdInURL;
  }

  getBuySellPlan(id: string) {
    this.overlayService.show('Loading');
    this.loading = true;

    this.buySellService.getBuySellPlan(id).subscribe({
      next: (res) => {
        this.buySellPlan = res;
        this.overlayService.hide();
        this.loading = false;
        this.buildForm();
      },
      error: (e) => { this.overlayService.hide(); this.loading = false; },
      complete: () => this.overlayService.hide()
    });
  }

  backToBuySellPlans() {
    if (!this.planFormPristine) {
      this.openConfirmCloseDialog();
    }
    else {
      this.router.navigate([`TimeOff${routes.BUY_SELL}${routes.BUY_SELL_PLANS}`]);
    }
  }

  formDataEmitted(formData) {
    this.planFormData = formData;
  }

  formStatusUpdated(formValid) {
    this.planFormValid = formValid;
  }

  formPristineEmitted(formPristine: boolean) {
    this.planFormPristine = formPristine;
  }

  save() {
    this.overlayService.show();

    if (this.planFormValid) {
      if (this.buySellPlan) {
        this.buySellService.updateBuySellPlan(this.buySellPlan.id, this.planFormData)
          .subscribe(
            {
              next: (v) => {
                this.overlayService.hide();
                this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
                this.planFormPristine = true;
              },
              error: (e) => {
                this.overlayService.hide();
              }
            }
          );
      }
      else {
        this.buySellService.createBuySellPlan(this.planFormData)
          .subscribe(
            {
              next: (v) => {
                this.overlayService.hide();
                this.snackbarService.openSnackBar(`${this.translate.instant('CreatedSuccessfully')}`, 'clear', 'success');
                this.router.navigate([`TimeOff${routes.BUY_SELL}${routes.BUY_SELL_PLANS}${routes.EDITOR}/${v.id}`]);
              },
              error: (e) => {
                this.overlayService.hide();
              }
            }
          );
      }
    }
  }

  refresh() {
    const id = this.getIdInURL();

    if (id) {
      this.getBuySellPlan(id);
    }
    else {
      this.buildForm();
    }
  }

  openConfirmCloseDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      text: this.translate.instant('UnsavedChangesMessage')
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.router.navigate([`${routes.SITE_SETTINGS_NO_SLASH}${routes.TIME_OFF}${routes.BUY_SELL_PLANS}`]);
        }
      }
    );
  }

  openConfirmPublishDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      localizationCode: 'ConfirmBuySellPublishMsg'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(
      data => {
        if (data === true) {
          this.publish();
        }
      }
    );
  }

  publish() {
    if (this.buySellPlan) {
      this.overlayService.show('Publishing');
      this.buySellService.publishBuySellPlan(this.buySellPlan.id)
        .subscribe(
          {
            next: (v) => {
              this.overlayService.hide();
              this.snackbarService.openSnackBar(`${this.translate.instant('PublishedSuccessfully')}`, 'clear', 'success');
              this.refresh();
            },
            error: (e) => {
              this.overlayService.hide();
            }
          }
        );
    }
  }

  openPublishHistoryDialog() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;

    dialogConfig.data = {
      buySellPlanId: this.buySellPlan.id
    };

    const dialogRef = this.dialog.open(BuySellPlanPublishHistoryDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe();
  }

}
